import React from 'react';
import { Link } from 'react-router-dom';
import semicircle from '../assets/circle.svg';
import cloudMigrationIcon from '../assets/cloud-migration.svg';
import cicdIcon from '../assets/ci-cd.svg';
import cloudComplianceIcon from '../assets/cloud-compliance.svg';
import testingIcon from '../assets/services/testingIcon.svg';
import finopsIcon from '../assets/cloud-cost-optimisation.svg';
import kubernetesIcon from '../assets/services/kubernetesIcon.svg';
import sreIcon from '../assets/services/sreIcon.svg';
import prometheusIcon from '../assets/services/grafanaIcon.svg';
import policyIcon from '../assets/services/policyIcon.svg';
import gitopsIcon from '../assets/services/gitopsIcon.svg';
import infrastructureIcon from '../assets/services/infraIcon.svg';
import backupIcon from '../assets/services/backupIcon.svg';
import aiIcon from '../assets/services/aiIcon.svg';
import mlopsIcon from '../assets/services/mlopsIcon.svg';
import '../styles/OurServices.css';

const ArrowIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 12H20M20 12L14 6M20 12L14 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const OurServicesSection: React.FC = () => {
  const allServicesData = [
    {
      id: 'cloud-cost-optimization',
      icon: finopsIcon,
      title: 'Cloud Cost Optimization & FinOps',
      description: 'Maximize cloud ROI with expert-led FinOps strategies achieving 30-60% cost reduction without sacrificing performance.',
      link: '/services/cloud-cost-optimization',
      hoverContent: {
        paragraph: "Cloud Cost Optimization & FinOps",
        listItems: [
          "30-60% Cost Reduction",
          "Real-Time Monitoring",
          "Multi-Cloud Governance",
          "Kubernetes Cost Breakdown"
        ]
      }
    },
    {
      id: 'kubernetes-consulting',
      icon: kubernetesIcon,
      title: 'Kubernetes Consulting',
      description: 'Expert Kubernetes design, deployment, and operations for robust container platforms driving agility, scalability, and resilience.',
      link: '/services/kubernetes-consulting',
      hoverContent: {
        paragraph: "Kubernetes Consulting",
        listItems: [
          "Multi-Regional Architecture",
          "Production-Grade Clusters",
          "Migration & Modernization",
          "Day-2 Operations"
        ]
      }
    },
    {
      id: 'ai-genai-integration',
      icon: aiIcon,
      title: 'AI & GenAI Integration',
      description: 'Integrate LLMs and generative AI into your products with security, observability, and cost control built in from day one.',
      link: '/services/ai-genai-integration',
      hoverContent: {
        paragraph: "AI & GenAI Integration",
        listItems: [
          "LLM & RAG Applications",
          "AI Security & Guardrails",
          "Governed AI Gateway",
          "Automated Evaluations"
        ]
      }
    },
    {
      id: 'mlops-platform-engineering',
      icon: mlopsIcon,
      title: 'MLOps & ML Platform Engineering',
      description: 'Automated ML pipelines, scalable GPU infrastructure, and monitored model deployments — from notebook to production.',
      link: '/services/mlops-platform-engineering',
      hoverContent: {
        paragraph: "MLOps & ML Platform Engineering",
        listItems: [
          "Automated ML Pipelines",
          "GPU Cost Optimization",
          "Model Serving at Scale",
          "Drift Detection"
        ]
      }
    },
    {
      id: 'penetration-testing',
      icon: testingIcon,
      title: 'Penetration Testing',
      description: 'Professional security testing with actionable remediation guidance following industry-standard methodologies.',
      link: '/services/penetration-testing',
      hoverContent: {
        paragraph: "Penetration Testing",
        listItems: [
          "OWASP Standards",
          "CVSS Scoring",
          "Cloud-Native Testing",
          "Actionable Results"
        ]
      }
    },
    {
      id: 'sre-consulting',
      icon: sreIcon,
      title: 'SRE Consulting Services',
      description: 'Site Reliability Engineering built for scale, speed, and stability using modern SRE practices and automation.',
      link: '/services/sre-consulting',
      hoverContent: {
        paragraph: "SRE Consulting Services",
        listItems: [
          "Production Readiness",
          "Observability Architecture",
          "Incident Management",
          "Release Engineering"
        ]
      }
    },
    {
      id: 'prometheus-grafana',
      icon: prometheusIcon,
      title: 'Prometheus & Grafana Consulting',
      description: 'Advanced monitoring and observability platform with real-time metrics, alerting, and rich dashboards.',
      link: '/services/prometheus-grafana',
      hoverContent: {
        paragraph: "Prometheus & Grafana Consulting",
        listItems: [
          "High-Scale Deployments",
          "SLO Management",
          "Long-Term Storage",
          "Multi-Cloud Monitoring"
        ]
      }
    },
    {
      id: 'policy-as-code',
      icon: policyIcon,
      title: 'Policy-as-Code Services',
      description: 'Automated governance and compliance across infrastructure, Kubernetes, CI/CD, and application delivery pipelines.',
      link: '/services/policy-as-code',
      hoverContent: {
        paragraph: "Policy-as-Code Services",
        listItems: [
          "Multi-Layer Coverage",
          "Automation-First",
          "Compliance Integration",
          "Real-Time Enforcement"
        ]
      }
    },
    {
      id: 'gitops-consulting',
      icon: gitopsIcon,
      title: 'GitOps Implementation Services',
      description: 'Build reliable, auditable, and fast software delivery with GitOps principles for predictability and automation.',
      link: '/services/gitops-consulting',
      hoverContent: {
        paragraph: "GitOps Implementation Services",
        listItems: [
          "Progressive Delivery",
          "Multi-Cluster Management",
          "Security-First",
          "Automated Rollback"
        ]
      }
    },
    {
      id: 'infrastructure-design',
      icon: infrastructureIcon,
      title: 'Infrastructure Design & Delivery',
      description: 'Build infrastructure that\'s scalable, secure, and ready for growth with cloud-native and automation-first platforms.',
      link: '/services/infrastructure-design',
      hoverContent: {
        paragraph: "Infrastructure Design & Delivery",
        listItems: [
          "Cloud-Native Design",
          "Security Built-In",
          "Scalability Focus",
          "End-to-End Implementation"
        ]
      }
    },
    {
      id: 'cloud-migration',
      icon: cloudMigrationIcon,
      title: 'Cloud Migration Services',
      description: 'Comprehensive, end-to-end solution for moving workloads smoothly and securely to cloud environments with minimal downtime.',
      link: '/services/cloud-migration',
      hoverContent: {
        paragraph: "Cloud Migration Services",
        listItems: [
          "Zero-Downtime Strategies",
          "Multi-Cloud Approaches",
          "Legacy Modernization",
          "Cost Optimization"
        ]
      }
    },
    {
      id: 'cloud-compliance',
      icon: cloudComplianceIcon,
      title: 'Cloud Compliance & Security',
      description: 'End-to-end compliance readiness for SOC 2, HIPAA, GDPR, ISO 27001, and PCI DSS without third-party SaaS dependencies.',
      link: '/services/cloud-compliance',
      hoverContent: {
        paragraph: "Cloud Compliance & Security",
        listItems: [
          "SOC 2, ISO 27001, HIPAA",
          "No SaaS Dependencies",
          "Automated Monitoring",
          "Security-First"
        ]
      }
    },
    {
      id: 'ci-cd-services',
      icon: cicdIcon,
      title: 'CI/CD Services',
      description: 'Robust, end-to-end automation framework that accelerates software delivery, data integration, and ML model deployment.',
      link: '/services/ci-cd-services',
      hoverContent: {
        paragraph: "CI/CD Services",
        listItems: [
          "Multi-Tool Expertise",
          "Security Integration",
          "Automated Testing",
          "GitOps Integration"
        ]
      }
    },
    {
      id: 'backup-disaster-recovery',
      icon: backupIcon,
      title: 'Backup & Disaster Recovery',
      description: 'Cloud-native, Kubernetes-aware backup and disaster recovery strategies that align with business continuity goals.',
      link: '/services/backup-disaster-recovery',
      hoverContent: {
        paragraph: "Backup & Disaster Recovery",
        listItems: [
          "Kubernetes-Native",
          "Multi-Cloud DR",
          "Compliance-Ready",
          "Automated Testing"
        ]
      }
    }
  ];

  // Show only first 8 services on homepage
  const servicesData = allServicesData.slice(0, 8);
  const hasMoreServices = allServicesData.length > 8;

  return (
    <section className="our-services-section">
      <div className="our-services-container">
        <div className="our-services-header">
          <h2 className="our-services-title">Our Services</h2>
          <p className="our-services-subtitle">Comprehensive technology solutions tailored to your needs</p>
        </div>

        <div className="our-services-grid">
          {servicesData.map((service) => (
            <div key={service.id} className="our-services-card">
              <div className='our-services-default-content-home'>
                <div className="our-services-icon">
                  <img src={service.icon} alt={service.title} />
                </div>
                <h3 className="our-service-title">{service.title}</h3>
                <p className="our-service-description">
                  {service.description}
                </p>
              </div>

              {/* Hover content */}
              <div className="our-services-hover-content-home">
                <div className="hover-text-column-home">
                  <p className='hover-text-column-text-home'>{service.hoverContent.paragraph}</p>
                  <ul className="home-services-list">
                    {service.hoverContent.listItems.map((item, i) => (
                      <li key={`${service.id}-item-${i}`}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>


              <Link to={service.link} className="our-service-link">
                Learn More
                <ArrowIcon />
              </Link>
            </div>
          ))}
        </div>

        {hasMoreServices && (
          <div className="our-services-footer">
            <Link to="/services" className="show-all-services-button">
              Show All Services
              <ArrowIcon />
            </Link>
          </div>
        )}
      </div>

      <div className="geometric-shape-our-service shape-our-services">
        <img src={semicircle} alt="background shape" className="semicircle-bg" />
      </div>
    </section>
  );
};

export default OurServicesSection;
